/**
 * ReceiptUploadPage — /receipts/<token>
 *
 * The one-purpose upload page behind a portal link: take a photo or pick
 * files, press Upload, done. Shows only a label ("My receipts" or the
 * task's name) — nothing else about the organisation. Honours the tenant's
 * access mode: open, PIN, or sign-in (sign-in still only reaches this page).
 */
import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Heading, Text, VStack, HStack, Input, Badge, Spinner, Alert, AlertIcon, List, ListItem, PinInput, PinInputField } from '@chakra-ui/react';
import { FiCamera, FiUpload, FiCheckCircle, FiLock } from 'react-icons/fi';
import { APP_VERSION } from '../version';

const API = process.env.REACT_APP_API_URL || '/api';

export default function ReceiptUploadPage() {
  const token = window.location.pathname.split('/').filter(Boolean)[1] || '';
  const [meta, setMeta] = useState(null);
  const [err, setErr] = useState(null);
  const [files, setFiles] = useState([]);
  const [pin, setPin] = useState('');
  const [login, setLogin] = useState({ email: '', password: '', token: null, mfa: '' });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const camRef = useRef(null), fileRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/receipts/${token}`).then(async r => { const d = await r.json(); if (!r.ok) throw new Error(d.error || 'Invalid link'); setMeta(d); })
      .catch(e => setErr(e.message));
  }, [token]);

  const addFiles = (list) => {
    const arr = Array.from(list || []).filter(f => !/\.(zip|7z|rar|tar|gz|tgz)$/i.test(f.name));
    setFiles(prev => [...prev, ...arr].slice(0, 10));
  };

  const signIn = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await fetch(`${API}/auth/login`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email: login.email, password: login.password, mfa_code: login.mfa || undefined }) });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Sign in failed');
      if (d.mfa_required && !d.token) { setLogin(l => ({ ...l, needMfa: true })); setBusy(false); return; }
      setLogin(l => ({ ...l, token: d.token }));
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  const send = async () => {
    if (!files.length) return;
    setBusy(true); setErr(null);
    try {
      const fd = new FormData();
      files.forEach(f => fd.append('files', f));
      const headers = {};
      if (meta.access_mode === 'pin') headers['x-portal-pin'] = pin;
      if (meta.access_mode === 'login' && login.token) headers.Authorization = `Bearer ${login.token}`;
      const r = await fetch(`${API}/receipts/${token}/upload`, { method: 'POST', headers, body: fd });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Upload failed');
      setDone(d); setFiles([]);
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  const Shell = ({ children }) => (
    <Box minH="100vh" bg="gray.50" display="flex" alignItems="flex-start" justifyContent="center" p={4}>
      <Box w="100%" maxW="480px" bg="white" borderRadius="lg" shadow="md" p={5} mt={{ base: 2, md: 10 }}>
        <HStack mb={3} spacing={2}><Heading size="sm" color="teal.700">ForgeBOS</Heading><Badge fontSize="9px">receipts</Badge></HStack>
        {children}
        <Text fontSize="9px" color="gray.400" mt={6} textAlign="center">ForgeBOS v{APP_VERSION}</Text>
      </Box>
    </Box>
  );

  if (err && !meta) return <Shell><Alert status="error" borderRadius="md"><AlertIcon />{err}</Alert></Shell>;
  if (!meta) return <Shell><Spinner /></Shell>;

  const gated = (meta.access_mode === 'pin' && pin.length < 4) || (meta.access_mode === 'login' && !login.token);

  return (
    <Shell>
      <Text fontSize="xs" color="gray.500" textTransform="uppercase" fontWeight="700">Upload to</Text>
      <Heading size="md" mb={1}>{meta.label}</Heading>
      <Text fontSize="xs" color="gray.500" mb={4}>{meta.tenant_name}</Text>

      {meta.access_mode === 'pin' && (
        <Box mb={4} p={3} bg="gray.50" borderRadius="md">
          <HStack mb={2}><FiLock /><Text fontSize="sm" fontWeight="600">Enter the upload PIN</Text></HStack>
          <HStack><PinInput value={pin} onChange={setPin} size="md" mask>{[0,1,2,3,4,5].map(i => <PinInputField key={i} />)}</PinInput></HStack>
        </Box>
      )}
      {meta.access_mode === 'login' && !login.token && (
        <Box mb={4} p={3} bg="gray.50" borderRadius="md">
          <HStack mb={2}><FiLock /><Text fontSize="sm" fontWeight="600">Sign in to upload</Text></HStack>
          <VStack spacing={2}>
            <Input size="sm" placeholder="Email" value={login.email} onChange={e => setLogin(l => ({ ...l, email: e.target.value }))} />
            <Input size="sm" type="password" placeholder="Password" value={login.password} onChange={e => setLogin(l => ({ ...l, password: e.target.value }))} />
            {login.needMfa && <Input size="sm" placeholder="Authenticator code" value={login.mfa} onChange={e => setLogin(l => ({ ...l, mfa: e.target.value }))} />}
            <Button size="sm" colorScheme="teal" w="100%" onClick={signIn} isLoading={busy}>Sign in</Button>
          </VStack>
        </Box>
      )}

      {done && (
        <Alert status="success" borderRadius="md" mb={4} alignItems="flex-start">
          <AlertIcon as={FiCheckCircle} />
          <Box fontSize="sm">
            <Text fontWeight="600">Uploaded to {done.destination}.</Text>
            <List fontSize="xs" mt={1}>{done.uploaded.map((u, i) => <ListItem key={i}>{u.name}{u.supplier ? ` — ${u.supplier}` : ''}{u.total != null ? ` £${(+u.total).toFixed(2)}` : ''}{!u.read ? ' (will be read when sorted)' : ''}</ListItem>)}</List>
          </Box>
        </Alert>
      )}
      {err && <Alert status="error" borderRadius="md" mb={3} fontSize="sm"><AlertIcon />{err}</Alert>}

      <VStack spacing={3} align="stretch" opacity={gated ? 0.5 : 1} pointerEvents={gated ? 'none' : 'auto'}>
        <input ref={camRef} type="file" accept="image/*" capture="environment" multiple hidden onChange={e => { addFiles(e.target.files); e.target.value = ''; }} />
        <input ref={fileRef} type="file" accept=".pdf,image/*,.docx,.csv,.txt,.heic" multiple hidden onChange={e => { addFiles(e.target.files); e.target.value = ''; }} />
        <Button size="lg" colorScheme="teal" leftIcon={<FiCamera />} onClick={() => camRef.current && camRef.current.click()}>Take a photo</Button>
        <Button size="lg" variant="outline" leftIcon={<FiUpload />} onClick={() => fileRef.current && fileRef.current.click()}>Choose files</Button>
        {files.length > 0 && (
          <Box p={2} border="1px dashed" borderColor="gray.300" borderRadius="md" fontSize="xs">
            {files.map((f, i) => <HStack key={i} justify="space-between"><Text isTruncated>{f.name}</Text><Button size="xs" variant="ghost" onClick={() => setFiles(prev => prev.filter((_, j) => j !== i))}>remove</Button></HStack>)}
          </Box>
        )}
        <Button size="lg" colorScheme="green" isDisabled={!files.length} isLoading={busy} onClick={send}>Upload {files.length ? `${files.length} file${files.length === 1 ? '' : 's'}` : ''}</Button>
        <Text fontSize="10px" color="gray.500" textAlign="center">PDF, photos, Word, CSV or text. Archives are not accepted. Up to 10 files, 50 MB each. Sort the details later in ForgeBOS.</Text>
      </VStack>
    </Shell>
  );
}
